import {
  failOperation,
  startOperation,
  succeedOperation,
} from '@mint-lib/datadog';

/**
 * Attributes of an `import-hub.upload` operation. The package serves both
 * data-ingestion APIs, and `importHubV2` switches the endpoints but not the
 * flow, so the flag goes here rather than into the name.
 */
type UploadOperationContext = {
  importHubV2: boolean;
  /** Derived from the file extension; the dialog accepts only these two. */
  fileFormat: 'csv' | 'xlsx';
};

type OpenUpload = { key: string; context: UploadOperationContext };

// Module state rather than React state: the upload dialog starts the
// operation and closes on the first parse progress event, while the data file
// socket (`useAsyncUpload`, in the wizard's `DataFileProvider`) reports the
// parse result that ends it. By data file id, because that socket subscribes
// to one data file and its events carry no id. Every attempt gets its own key.
const openUploads = new Map<string, OpenUpload>();

const takeOpenUpload = (dataFileId: string) => {
  const openUpload = openUploads.get(dataFileId);
  openUploads.delete(dataFileId);
  return openUpload;
};

/** Fails the open upload of the data file, if any, as `abandoned`. */
export function abandonUploadOperation(dataFileId: string) {
  const openUpload = takeOpenUpload(dataFileId);
  if (!openUpload) return;
  failOperation('import-hub.upload', 'abandoned', openUpload);
}

/**
 * Starts `import-hub.upload` when the upload dialog sends a file (Next, or the
 * file's Retry button). `retry` marks an attempt after a failed one in the
 * same dialog, so each attempt is measured on its own. A start while an
 * upload of the same data file is still open replaces that file, so the old
 * operation is abandoned.
 */
export function startUploadOperation(
  dataFileId: string,
  context: UploadOperationContext,
  { retry }: { retry: boolean },
) {
  abandonUploadOperation(dataFileId);
  const key = crypto.randomUUID();
  openUploads.set(dataFileId, { key, context });
  startOperation('import-hub.upload', {
    key,
    context,
    description: retry ? 'retry' : undefined,
  });
}

/** Call when the socket reports that the file was parsed. */
export function succeedUploadOperation(dataFileId: string) {
  const openUpload = takeOpenUpload(dataFileId);
  if (!openUpload) return;
  succeedOperation('import-hub.upload', openUpload);
}

/**
 * Call when the S3 upload or the parse request fails, or the socket reports a
 * failed parse with the backend's error type (e.g. `RowExceedError`). Parse
 * events with no open upload (the sheet selection dialog re-parses the same
 * file) are ignored.
 */
export function failUploadOperation(dataFileId: string, errorType?: string) {
  const openUpload = takeOpenUpload(dataFileId);
  if (!openUpload) return;
  failOperation('import-hub.upload', 'error', {
    key: openUpload.key,
    context: errorType
      ? { ...openUpload.context, errorType }
      : openUpload.context,
  });
}

/**
 * Forgets every open operation without reporting it. For tests.
 * @public
 */
export function resetUploadOperations() {
  openUploads.clear();
}
